import React, { useContext, useState } from 'react'
// node.js library that concatenates classes (strings)
// react plugin used to create charts
import { Line } from 'react-chartjs-2'
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded'
import PaymentsRounded from '@mui/icons-material/PaymentsRounded'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import CancelRounded from '@mui/icons-material/CancelRounded'
import TaskAltRounded from '@mui/icons-material/TaskAltRounded'
import GroupRounded from '@mui/icons-material/GroupRounded'
import TimerRounded from '@mui/icons-material/TimerRounded'
import StarRounded from '@mui/icons-material/StarRounded'
import EmojiEventsRounded from '@mui/icons-material/EmojiEventsRounded'
import ShowChartRounded from '@mui/icons-material/ShowChartRounded'
import { SectionCard, StatTile, raisedButtonSx } from '../components/UI/Depth'
import ConfigurationContext from '../context/Configuration'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

import { Box, Typography, Input, Button, Grid, useTheme } from '@mui/material'
import Header from '../components/Headers/Header'
import { useQuery, gql } from '@apollo/client'
import { restaurantDashboard } from '../apollo'

import useGlobalStyles from '../utils/globalStyles'
import { withTranslation } from 'react-i18next'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

// Same query the store app's dashboard reads, so a store and the admin panel
// always see identical figures for the same dates.
const GET_RESTAURANT_DASHBOARD = gql`
  ${restaurantDashboard}
`

const localDay = value => {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const minutesText = m =>
  m === null || m === undefined
    ? '—'
    : m < 60
    ? `${Math.round(m)} min`
    : `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`

const changeText = (current, previous) => {
  if (!previous) return current ? 'new' : '0%'
  const change = ((current - previous) / previous) * 100
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`
}

const Dashboard = props => {
  const { t } = props
  const theme = useTheme()
  const restaurantId = localStorage.getItem('restaurantId')

  const intializeStartDate = () => {
    var d = new Date()
    d.setDate(d.getDate() - 7)
    return d.toISOString().substr(0, 10)
  }
  // `draft` follows the date inputs; queries only re-run on Apply.
  const [draft, setDraft] = useState({
    startingDate: intializeStartDate(),
    endingDate: new Date().toISOString().substr(0, 10)
  })
  const [stateData, setStateData] = useState(draft)

  // Both dates are inclusive whole days in the browser's timezone
  const start = localDay(stateData.startingDate)
  const end = localDay(stateData.endingDate)
  end.setDate(end.getDate() + 1)

  const { data, error, loading } = useQuery(GET_RESTAURANT_DASHBOARD, {
    variables: {
      restaurant: restaurantId,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      timezoneOffset: new Date().getTimezoneOffset()
    },
    fetchPolicy: 'cache-and-network'
  })
  const dashboard = data?.restaurantDashboard
  const cur = dashboard?.current
  const prev = dashboard?.previous

  const globalClasses = useGlobalStyles()
  const { currencySymbol = '' } = useContext(ConfigurationContext) || {}
  const money = value => `${currencySymbol}${(Number(value) || 0).toFixed(2)}`
  const show = fn => (loading && !dashboard ? '...' : cur ? fn() : '—')
  const vsPrevious = (current, previous, format = v => v) =>
    cur
      ? `${changeText(current, previous)} · ${t('DashVsPrevious')}: ${format(
          previous
        )}`
      : null

  const statCards = [
    {
      label: t('DashEarned'),
      value: show(() => money(cur.earnings)),
      hint: vsPrevious(cur?.earnings, prev?.earnings, money),
      icon: PaymentsRounded,
      tone: 'orange'
    },
    {
      label: t('DashSuccessfulOrders'),
      value: show(() => cur.successfulOrders),
      hint: vsPrevious(cur?.successfulOrders, prev?.successfulOrders),
      icon: CheckCircleRounded,
      tone: 'green'
    },
    {
      label: t('DashAvgOrderValue'),
      value: show(() => money(cur.averageOrderValue)),
      hint: vsPrevious(cur?.averageOrderValue, prev?.averageOrderValue, money),
      icon: ReceiptLongRounded,
      tone: 'blue'
    },
    {
      label: t('DashCancelledOrders'),
      value: show(() => cur.cancelledOrders),
      hint: vsPrevious(cur?.cancelledOrders, prev?.cancelledOrders),
      icon: CancelRounded,
      tone: 'rose'
    }
  ]

  const insightCards = [
    {
      label: t('DashCompletionRate'),
      value: show(() => `${Math.round(cur.completionRate)}%`),
      hint: cur ? t('DashInProgress', { count: cur.inProgressOrders }) : null,
      icon: TaskAltRounded,
      tone: 'green'
    },
    {
      label: t('DashCustomers'),
      value: show(() => cur.uniqueCustomers),
      hint: cur
        ? t('DashNewReturning', {
            fresh: cur.newCustomers,
            returning: cur.returningCustomers
          })
        : null,
      icon: GroupRounded,
      tone: 'violet'
    },
    {
      label: t('DashAvgPrepTime'),
      value: show(() => minutesText(cur.avgPrepMinutes)),
      hint: cur
        ? `${t('DashAvgAcceptTime')}: ${minutesText(cur.avgAcceptMinutes)}`
        : null,
      icon: TimerRounded,
      tone: 'amber'
    },
    {
      label: t('DashRating'),
      value: show(() =>
        dashboard.reviewCount ? dashboard.rating.toFixed(1) : '—'
      ),
      hint: dashboard
        ? t('DashBasedOnReviews', { count: dashboard.reviewCount })
        : null,
      icon: StarRounded,
      tone: 'orange'
    }
  ]

  const dateInputSx = { mt: 0, width: { xs: '100%', sm: 170 } }
  const rangeFilter = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        flexWrap: 'wrap'
      }}>
      <Box sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.disabled' }}>
          {t('StartDate')}
        </Typography>
        <Input
          type="date"
          inputProps={{ max: draft.endingDate }}
          onChange={event =>
            setDraft({ ...draft, startingDate: event.target.value })
          }
          value={draft.startingDate}
          disableUnderline
          className={globalClasses.input}
          sx={dateInputSx}
        />
      </Box>
      <Box sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: 'text.disabled' }}>
          {t('EndDate')}
        </Typography>
        <Input
          type="date"
          inputProps={{
            min: draft.startingDate,
            max: new Date().toISOString().substr(0, 10)
          }}
          onChange={event =>
            setDraft({ ...draft, endingDate: event.target.value })
          }
          value={draft.endingDate}
          disableUnderline
          className={globalClasses.input}
          sx={dateInputSx}
        />
      </Box>
      <Button
        disabled={
          draft.startingDate > draft.endingDate ||
          (draft.startingDate === stateData.startingDate &&
            draft.endingDate === stateData.endingDate)
        }
        onClick={() => setStateData(draft)}
        sx={{ ...raisedButtonSx, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
        {t('Apply')}
      </Button>
    </Box>
  )

  return (
    <>
      <Header action={rangeFilter} />
      {error ? (
        <Typography sx={{ color: 'error.main', mb: 2 }}>
          {error.message}
        </Typography>
      ) : null}

      <Grid container spacing={2.5} mb={2.5}>
        {statCards.map(card => (
          <Grid item xs={12} sm={6} lg={3} key={card.label}>
            <StatTile {...card} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2.5} mb={3}>
        {insightCards.map(card => (
          <Grid item xs={12} sm={6} lg={3} key={card.label}>
            <StatTile {...card} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard
            icon={ShowChartRounded}
            tone="orange"
            title={t('SalesAndOrders')}
            subtitle={`${stateData.startingDate} → ${stateData.endingDate}`}
            bodySx={{ pt: 3, height: 420 }}>
            <Line
              data={{
                labels: (dashboard?.daily || []).map(d => d.date.slice(5)),
                datasets: [
                  {
                    label: t('DashEarned'),
                    backgroundColor: theme.palette.secondary.darkest,
                    borderColor: theme.palette.secondary.darkest,
                    data: (dashboard?.daily || []).map(d => d.earnings),
                    tension: 0.35,
                    yAxisID: 'y'
                  },
                  {
                    label: t('DashSuccessfulOrders'),
                    backgroundColor: theme.palette.warning.dark,
                    borderColor: theme.palette.warning.dark,
                    data: (dashboard?.daily || []).map(d => d.successful),
                    tension: 0.35,
                    yAxisID: 'y1'
                  }
                ]
              }}
              options={{
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      boxWidth: 8,
                      padding: 18,
                      color: theme.palette.text.secondary,
                      font: { size: 12 }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: { color: theme.palette.grey[100] },
                    border: { display: false },
                    ticks: {
                      color: theme.palette.text.disabled,
                      font: { size: 12 },
                      callback: v => `${currencySymbol}${v}`
                    }
                  },
                  y1: {
                    beginAtZero: true,
                    position: 'right',
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      precision: 0,
                      color: theme.palette.text.disabled,
                      font: { size: 12 }
                    }
                  },
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      color: theme.palette.text.disabled,
                      font: { size: 12 }
                    }
                  }
                }
              }}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <SectionCard
            icon={EmojiEventsRounded}
            tone="amber"
            title={t('DashTopItems')}
            subtitle={
              cur ? t('DashItemsSold', { count: cur.itemsSold }) : undefined
            }
            bodySx={{ height: 420, overflowY: 'auto' }}>
            {!dashboard?.topItems?.length ? (
              <Typography sx={{ color: 'text.disabled', fontSize: 14 }}>
                {loading ? '...' : t('DashNoItems')}
              </Typography>
            ) : (
              dashboard.topItems.map((item, i) => {
                const top = dashboard.topItems[0].quantity || 1
                return (
                  <Box
                    key={`${item.title}-${i}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.25,
                      borderTop: i ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}>
                    <Box
                      component={item.image ? 'img' : 'div'}
                      src={item.image || undefined}
                      alt=""
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        objectFit: 'cover',
                        bgcolor: 'grey.100',
                        flexShrink: 0
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          noWrap
                          sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                          {`${i + 1}. ${item.title}`}
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                          ×{item.quantity}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.100',
                          my: 0.75,
                          overflow: 'hidden'
                        }}>
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(item.quantity / top) * 100}%`,
                            bgcolor: 'secondary.darkest',
                            borderRadius: 3
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                        {money(item.earnings)}
                      </Typography>
                    </Box>
                  </Box>
                )
              })
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </>
  )
}

export default withTranslation()(Dashboard)
